import React, { useEffect, useState, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import Select from "react-select";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function DoctorCreate() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [specializations, setSpecializations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [selectedRole, setSelectedRole] = useState("doctor");

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        contact_number: "",
        address: "",
        bio: "",
        img: null,
        specialization_id: [],
        availability: [
            { day_of_week: "", start_time: "", end_time: "" }
        ],
        role: "doctor"
    });

    const [errors, setErrors] = useState({});

    // Role options
    const roleOptions = [
        { value: "doctor", label: "Doctor" },
        { value: "sweeper", label: "Sweeper" }
    ];

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    const days = [
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
        { value: "Sunday", label: "Sunday" }
    ];

    const getAvailableDays = (currentIndex) => {
        const selectedDays = form.availability
            .map((avail, index) => index !== currentIndex ? avail.day_of_week : null)
            .filter(day => day);

        return days.filter(day => !selectedDays.includes(day.value));
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: "38px",
            borderRadius: "6px"
        })
    };

    useEffect(() => {
        fetchSpecializations();
    }, []);

    const fetchSpecializations = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/doctors-create`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );
            setSpecializations(res.data.specializations);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load specializations'));
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "img") {
            setForm({ ...form, img: files[0] });
        } else {
            setForm({ ...form, [name]: value });
        }
        // Clear error for this field when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRoleChange = (selectedOption) => {
        const role = selectedOption ? selectedOption.value : "doctor";
        setSelectedRole(role);

        // Reset form for sweeper - clear doctor-specific fields
        if (role === "sweeper") {
            setForm({
                ...form,
                role: role,
                contact_number: "",
                address: "",
                bio: "",
                specialization_id: [],
                availability: [{ day_of_week: "", start_time: "", end_time: "" }]
            });
        } else {
            setForm({
                ...form,
                role: role
            });
        }

        // Clear all errors when role changes
        setErrors({});
    };

    const handleAvailabilityChange = (index, field, value) => {
        const updated = [...form.availability];
        updated[index][field] = value;
        setForm({ ...form, availability: updated });
    };

    const addAvailabilityRow = () => {
        setForm({
            ...form,
            availability: [...form.availability, { day_of_week: "", start_time: "", end_time: "" }]
        });
        showSuccessToast(t('Availability row added'));
    };

    const removeAvailabilityRow = (index) => {
        if (form.availability.length === 1) {
            showWarningToast(t('You need at least one availability slot'));
            return;
        }
        const updated = form.availability.filter((_, i) => i !== index);
        setForm({ ...form, availability: updated });
        showSuccessToast(t('Availability row removed'));
    };

    const validateForm = () => {
        const newErrors = {};

        // Common validations for all roles
        if (!form.name.trim()) newErrors.name = [t('Name is required')];
        if (!form.email.trim()) newErrors.email = [t('Email is required')];
        if (!form.password.trim()) newErrors.password = [t('Password is required')];
        else if (form.password.length < 8) newErrors.password = [t('Password must be at least 8 characters')];

        // Email format validation
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = [t('Please enter a valid email address')];
        }

        // Doctor-specific validations
        if (form.role === "doctor") {
            if (!form.contact_number.trim()) newErrors.contact_number = [t('Contact number is required')];
            if (!form.address.trim()) newErrors.address = [t('Address is required')];

            // Validate availability for doctors
            for (let i = 0; i < form.availability.length; i++) {
                const avail = form.availability[i];
                if (!avail.day_of_week) {
                    newErrors[`availability_${i}_day`] = [t('Day is required')];
                }
                if (!avail.start_time) {
                    newErrors[`availability_${i}_start`] = [t('Start time is required')];
                }
                if (!avail.end_time) {
                    newErrors[`availability_${i}_end`] = [t('End time is required')];
                }
                if (avail.start_time && avail.end_time && avail.start_time >= avail.end_time) {
                    newErrors[`availability_${i}_time`] = [t('Start time must be before end time')];
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showErrorToast(t('Please fix the errors in the form'));
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const formData = new FormData();

            // Common fields for all roles
            formData.append("name", form.name);
            formData.append("email", form.email);
            formData.append("password", form.password);
            formData.append("role", form.role);

            // Image (optional for all roles)
            if (form.img) {
                formData.append("img", form.img);
            }

            // Doctor-specific fields
            if (form.role === "doctor") {
                formData.append("contact_number", form.contact_number);
                formData.append("address", form.address);
                formData.append("bio", form.bio || "");

                // Specializations
                form.specialization_id.forEach((id, i) => {
                    formData.append(`specialization_id[${i}]`, id);
                });

                // Availability
                form.availability.forEach((item, i) => {
                    formData.append(`availability[${i}][day_of_week]`, item.day_of_week);
                    formData.append(`availability[${i}][start_time]`, item.start_time);
                    formData.append(`availability[${i}][end_time]`, item.end_time);
                });
            }

            await axios.post(
                `${process.env.REACT_APP_API_URL}/doctors`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const successMessage = form.role === "doctor"
                ? t('Doctor created successfully!')
                : t('Sweeper created successfully!');

            showSuccessToast(successMessage);

            setTimeout(() => {
                navigate('/doctors');
            }, 1500);

        } catch (err) {
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
                showErrorToast(t('Please fix the validation errors'));
            } else if (err.response?.status === 500) {
                showErrorToast(t('Server error. Please try again later.'));
            } else {
                showErrorToast(t('Failed to create user'));
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Check if current role is doctor
    const isDoctor = form.role === "doctor";

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="p-4 shadow-sm" style={{ borderRadius: "12px" }}>
                        <h4 className="mb-4">{t("Create User")}</h4>

                        <Form onSubmit={handleSubmit}>

                            {/* Role Selection */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={roleOptions}
                                        value={roleOptions.find(opt => opt.value === form.role)}
                                        onChange={handleRoleChange}
                                        placeholder={t('Select role')}
                                    />
                                    {errors.role && (
                                        <small className="text-danger">{errors.role?.[0]}</small>
                                    )}
                                </Col>
                            </Row>

                            {/* Name + Email + Password */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                        placeholder={t('Enter name')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={4}>
                                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                        placeholder="user@example.com"
                                        type="email"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={4}>
                                    <Form.Label>Profile Image</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        onChange={handleChange}
                                        accept="image/*"
                                    />
                                    <small className="text-muted">{t('Upload a profile picture (optional)')}</small>
                                </Col>
                            </Row>




                            {/* Doctor-specific fields - ONLY shown when role is doctor */}
                            {isDoctor && (
                                <>
                                    {/* Contact + Address */}
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    name="password"
                                                    value={form.password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.password}
                                                    placeholder={t('Enter password')}
                                                    type={showPassword ? "text" : "password"}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn position-absolute end-0 top-0"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        padding: '0.375rem 0.75rem',
                                                        color: '#6c757d',
                                                        top: '50%',
                                                        transform: 'translateY(10%)',
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.password?.[0]}
                                                </Form.Control.Feedback>
                                            </div>
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Contact Number <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                name="contact_number"
                                                value={form.contact_number}
                                                onChange={handleChange}
                                                isInvalid={!!errors.contact_number}
                                                placeholder={t('Enter contact number')}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.contact_number?.[0]}
                                            </Form.Control.Feedback>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                name="address"
                                                value={form.address}
                                                onChange={handleChange}
                                                isInvalid={!!errors.address}
                                                placeholder={t('Enter address')}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.address?.[0]}
                                            </Form.Control.Feedback>
                                        </Col>
                                    </Row>

                                    {/* Bio */}
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Bio</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="bio"
                                                value={form.bio}
                                                onChange={handleChange}
                                                placeholder={t('Enter doctor bio')}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Specializations */}
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Specializations</Form.Label>
                                            <Select
                                                isMulti
                                                styles={customSelectStyles}
                                                options={specializations.map(sp => ({
                                                    value: sp.id,
                                                    label: sp.name
                                                }))}
                                                onChange={(selected) =>
                                                    setForm({
                                                        ...form,
                                                        specialization_id: selected ? selected.map(i => i.value) : []
                                                    })
                                                }
                                                placeholder={t('Select specializations')}
                                            />
                                            <small className="text-muted">{t('You can select multiple specializations')}</small>
                                        </Col>
                                    </Row>

                                    {/* Availability */}
                                    <h5 className="mt-3 mb-3">{t('Availability')} <span className="text-danger">*</span></h5>

                                    {form.availability.map((row, index) => (
                                        <div key={index}>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Label>Day</Form.Label>
                                                    <Select
                                                        styles={customSelectStyles}
                                                        options={getAvailableDays(index)}
                                                        value={days.find(d => d.value === row.day_of_week)}
                                                        onChange={(selected) =>
                                                            handleAvailabilityChange(index, "day_of_week", selected.value)
                                                        }
                                                        placeholder={t('Select day')}
                                                    />
                                                    {errors[`availability_${index}_day`] && (
                                                        <small className="text-danger">{errors[`availability_${index}_day`]?.[0]}</small>
                                                    )}
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label>Start Time</Form.Label>
                                                    <Form.Control
                                                        type="time"
                                                        value={row.start_time}
                                                        onChange={(e) =>
                                                            handleAvailabilityChange(index, "start_time", e.target.value)
                                                        }
                                                        isInvalid={!!errors[`availability_${index}_start`]}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[`availability_${index}_start`]?.[0]}
                                                    </Form.Control.Feedback>
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label>End Time</Form.Label>
                                                    <Form.Control
                                                        type="time"
                                                        value={row.end_time}
                                                        onChange={(e) =>
                                                            handleAvailabilityChange(index, "end_time", e.target.value)
                                                        }
                                                        isInvalid={!!errors[`availability_${index}_end`]}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[`availability_${index}_end`]?.[0]}
                                                    </Form.Control.Feedback>
                                                </Col>

                                                <Col md={2} className="d-flex align-items-end">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger w-100"
                                                        onClick={() => removeAvailabilityRow(index)}
                                                        text={t('Remove')}
                                                    />
                                                </Col>
                                            </Row>
                                            {errors[`availability_${index}_time`] && (
                                                <div className="mb-3">
                                                    <small className="text-danger">{errors[`availability_${index}_time`]?.[0]}</small>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-secondary mb-3"
                                        onClick={addAvailabilityRow}
                                        text={t('Add Availability')}
                                    />
                                </>
                            )}

                            {/* Submit Button */}
                            <div className="text-end">
                                <ButtonComponent
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={loading}
                                    text={loading ? t('Saving...') : t('Create User')}
                                />
                            </div>

                        </Form>
                    </Card>
                </Col>
            </Row>
        </PageLayout >
    );
}